import React, { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { BrandLogo, Button, Input, PageLoading } from "@/components/atoms";
import { ROUTES } from "@/constants";
import { useAuth, useTenantBrand } from "@/hooks";
import { ApiError } from "@/services";
import { redirectAfterLogin, resolvePostLoginTarget } from "@/utils";
import "./Login.scss";

interface LoginLocationState {
  from?: { pathname: string };
  message?: string;
}

const requiresCrossOriginRedirect = (target: string): boolean => {
  try {
    const url = new URL(target, window.location.origin);
    return url.origin !== window.location.origin;
  } catch {
    return false;
  }
};

/**
 * Shared login page for all backoffice roles
 */
export function Login() {
  const { login, isAuthenticated, user } = useAuth();
  const { getLogoSrc, logoAlt } = useTenantBrand();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LoginLocationState | null;
  const [error, setError] = useState(locationState?.message ?? "");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fromPath = locationState?.from?.pathname ?? null;

  useEffect(() => {
    if (locationState?.message) {
      setError(locationState.message);
    }
  }, [locationState?.message]);

  if (isAuthenticated && user) {
    const target = resolvePostLoginTarget(user, fromPath);

    if (requiresCrossOriginRedirect(target)) {
      window.location.replace(target);
      return <PageLoading />;
    }

    const path = target.startsWith(window.location.origin)
      ? target.slice(window.location.origin.length)
      : target;

    return <Navigate to={path} replace />;
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    setIsSubmitting(true);

    const data = new FormData(event.currentTarget);
    const email = data.get("email") as string;
    const password = data.get("password") as string;

    try {
      const authUser = await login(email, password);
      redirectAfterLogin(authUser, fromPath, navigate);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? (err.responseData?.message ?? err.message)
          : "Credenciales inválidas o error de conexión.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login">
      <div className="login__card">
        <BrandLogo
          className="login__logo"
          src={getLogoSrc("login")}
          alt={logoAlt}
        />

        <h1 className="login__title">Acceso al panel</h1>

        {error && <p className="login__error">{error}</p>}

        <form className="login__form" onSubmit={handleSubmit} noValidate>
          <div className="login__field">
            <label className="login__label" htmlFor="email">
              Correo electrónico
            </label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="admin@ejemplo.com"
              autoComplete="email"
              required
            />
          </div>

          <div className="login__field">
            <label className="login__label" htmlFor="password">
              Contraseña
            </label>
            <Input
              id="password"
              name="password"
              type="password"
              placeholder="••••••••"
              autoComplete="current-password"
              required
            />
          </div>

          <Button
            type="submit"
            variant="primary"
            fullWidth
            className="login__submit"
            disabled={isSubmitting}
            actionTrackSufix="bo_login"
            motionScale="base"
          >
            {isSubmitting ? "Ingresando..." : "Ingresar"}
          </Button>
        </form>

        <div
          className="login__back"
          onClick={() => navigate(ROUTES.public.home)}
        >
          <ArrowLeft size={14} /> Volver al sitio
        </div>
      </div>
    </div>
  );
};
